import axios from "axios";
import { format } from "date-fns";
import { apiUrl } from "../../../../dataProvider";
import { sortAlphabetically } from "../../../../utils/sortAlphabetically";

export const STEPS = [
  "Basic Info",
  "Variants",
  "Reorder",
  "Sizes",
  "Images",
  "Pricing",
  "Review",
];

export const PRICE_TYPE_STATIC = "static";
export const PRICE_TYPE_HYBRID = "hybrid";
export const PRICE_TYPE_VARIANT_STATIC = "variant_static";
export const PRICE_TYPE_VARIANT_HYBRID = "variant_hybrid";
export const PRICE_TYPE_DYNAMIC = "dynamic";

export const PRICE_TYPE_CHOICES = [
  { id: PRICE_TYPE_STATIC, name: "Single Static" },
  { id: PRICE_TYPE_HYBRID, name: "Single Hybrid" },
  { id: PRICE_TYPE_VARIANT_STATIC, name: "Variant Static" },
  { id: PRICE_TYPE_VARIANT_HYBRID, name: "Variant Hybrid" },
  { id: PRICE_TYPE_DYNAMIC, name: "Dynamic" },
];

const parseNullableNumber = (value) => {
  if (value === "" || value == null) return null;
  const num = Number(value);
  return isNaN(num) ? null : num;
};

export const buildPricingPayload = (values = {}) => {
  const priceType = values.price_type;
  if (!priceType) return null;

  if (priceType === PRICE_TYPE_STATIC) {
    return {
      price_type: priceType,
      base_price: values.base_price,
    };
  }

  if (priceType === PRICE_TYPE_HYBRID) {
    return {
      price_type: priceType,
      base_price: values.base_price,
      hybrid_precious_metal_type_id: values.hybrid_precious_metal_type_id,
      hybrid_precious_metal_karat: parseNullableNumber(
        values.hybrid_precious_metal_karat
      ),
      precious_metal_cost_percentage_in_product: parseNullableNumber(
        values.precious_metal_cost_percentage_in_product
      ),
      metal_price_baseline: values.metal_price_baseline,
    };
  }

  if (priceType === PRICE_TYPE_VARIANT_STATIC) {
    return {
      price_type: priceType,
      has_uniform_size_pricing: values.has_uniform_size_pricing ?? true,
    };
  }

  if (priceType === PRICE_TYPE_VARIANT_HYBRID) {
    return {
      price_type: priceType,
      has_uniform_size_pricing: values.has_uniform_size_pricing ?? true,
      precious_metal_cost_percentage_in_product: parseNullableNumber(
        values.precious_metal_cost_percentage_in_product
      ),
    };
  }

  if (priceType === PRICE_TYPE_DYNAMIC) {
    return {
      price_type: priceType,
      base_price: values.base_price,
    };
  }

  return {
    price_type: priceType,
  };
};

export const isPricingStepValid = (values = {}) => {
  const priceType = values.price_type;
  if (!priceType) return false;

  if (priceType === PRICE_TYPE_STATIC) {
    return values.base_price != null && values.base_price !== "";
  }

  if (priceType === PRICE_TYPE_HYBRID) {
    const hasBasePrice = values.base_price != null && values.base_price !== "";
    const hasMetalType =
      values.hybrid_precious_metal_type_id != null &&
      values.hybrid_precious_metal_type_id !== "";
    const hasBaseline =
      values.metal_price_baseline != null &&
      values.metal_price_baseline !== "";

    let isKaratValid = true;
    if (
      values.hybrid_precious_metal_karat != null &&
      values.hybrid_precious_metal_karat !== ""
    ) {
      const k = Number(values.hybrid_precious_metal_karat);
      isKaratValid = Number.isInteger(k) && k >= 1 && k <= 24;
    }

    let isPercentageValid = true;
    if (
      values.precious_metal_cost_percentage_in_product != null &&
      values.precious_metal_cost_percentage_in_product !== ""
    ) {
      const p = Number(values.precious_metal_cost_percentage_in_product);
      isPercentageValid = !isNaN(p) && p >= 0 && p <= 100;
    }

    return (
      hasBasePrice &&
      hasMetalType &&
      hasBaseline &&
      isKaratValid &&
      isPercentageValid
    );
  }

  if (priceType === PRICE_TYPE_VARIANT_STATIC) {
    return values.has_uniform_size_pricing != null;
  }

  if (priceType === PRICE_TYPE_VARIANT_HYBRID) {
    let isPercentageValid = true;
    if (
      values.precious_metal_cost_percentage_in_product != null &&
      values.precious_metal_cost_percentage_in_product !== ""
    ) {
      const p = Number(values.precious_metal_cost_percentage_in_product);
      isPercentageValid = !isNaN(p) && p >= 0 && p <= 100;
    }

    return values.has_uniform_size_pricing != null && isPercentageValid;
  }

  if (priceType === PRICE_TYPE_DYNAMIC) {
    return values.base_price != null && values.base_price !== "";
  }

  return true;
};

const mapStoreOptions = (payload) => {
  const list = Array.isArray(payload) ? payload : [];
  return sortAlphabetically(
    list.map((item) => ({
      ...item,
      store_name:
        item.store_name ||
        item.name ||
        item.store_number ||
        String(item.id ?? ""),
    })),
    "store_name"
  );
};

const mapInlayOptions = (payload) => {
  const list = Array.isArray(payload) ? payload : [];
  return sortAlphabetically(
    list.map((item) => ({
      ...item,
      name: item.name || item.inlay_name || item.sku_code || String(item.id ?? ""),
    })),
    "name"
  );
};

const mapGemstoneOptions = (payload) => {
  const list = Array.isArray(payload) ? payload : [];
  return sortAlphabetically(
    list.map((item) => ({
      ...item,
      name:
        item.name ||
        item.gemstone_name ||
        item.gem_stone_name ||
        item.sku_code ||
        String(item.id ?? ""),
    })),
    "name"
  );
};

export const PRESET_RESOURCE_MAP = {
  BASE_MATERIAL: {
    resource: "custom-pricing/ring-materials",
    optionLabel: "name",
  },
  INLAY: {
    resource: "custom-pricing/inlays",
    optionLabel: "name",
    transformOptions: mapInlayOptions,
  },
  FIRST_INLAY: {
    resource: "custom-pricing/inlays",
    optionLabel: "name",
    transformOptions: mapInlayOptions,
  },
  SECOND_INLAY: {
    resource: "custom-pricing/inlays",
    optionLabel: "name",
    transformOptions: mapInlayOptions,
  },
  GEMSTONE: {
    resource: "custom-pricing/gemstones",
    optionLabel: "name",
    transformOptions: mapGemstoneOptions,
  },
  FIRST_GEMSTONE: {
    resource: "custom-pricing/gemstones",
    optionLabel: "name",
    transformOptions: mapGemstoneOptions,
  },
  SECOND_GEMSTONE: {
    resource: "custom-pricing/gemstones",
    optionLabel: "name",
    transformOptions: mapGemstoneOptions,
  },
};

export const getOptionLabel = (item) =>
  item?.name ||
  item?.store_name ||
  item?.inlay_name ||
  item?.gemstone_name ||
  item?.gem_stone_name ||
  item?.sku_code ||
  String(item?.id ?? "");

export const getOptionSku = (item) =>
  item?.sku_code || String(item?.id ?? "");

export const buildProductPayload = (values = {}) => ({
  title: values.title,
  sku_code: typeof values.sku_code === "string" ? values.sku_code.trim() : values.sku_code,
  description: values.description ?? null,
  simple_product_category_id: values.simple_product_category_id,
  simple_product_line_id: values.simple_product_line_id ?? null,
  store_id: values.store_id ?? null,
  is_active: values.is_active ?? true,
});

export const buildOptionTypesPayload = (sortedGroups = []) => ({
  option_types: (sortedGroups || []).map((group, groupIndex) => ({
    ...(group.optionTypeId ? { id: group.optionTypeId } : {}),
    preset_id: group.presetId,
    sort_order: groupIndex + 1,
    option_values: (group.items || []).map((item, itemIndex) => ({
      ...(item.optionValueId ? { id: item.optionValueId } : {}),
      label: item.name || item.label || getOptionLabel(item),
      sku_code: getOptionSku(item),
      sort_order: itemIndex + 1,
    })),
  })),
});

export const normalizeOptionTypesList = (payload) => {
  if (Array.isArray(payload?.option_types)) return payload.option_types;
  if (Array.isArray(payload?.optionTypes)) return payload.optionTypes;
  if (Array.isArray(payload?.data?.option_types)) return payload.data.option_types;
  if (Array.isArray(payload?.data?.optionTypes)) return payload.data.optionTypes;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

export const getOptionTypeValues = (optionType) => {
  if (Array.isArray(optionType?.option_values)) return optionType.option_values;
  if (Array.isArray(optionType?.optionValues)) return optionType.optionValues;
  return [];
};

export const getOptionTypePresetId = (optionType) =>
  optionType?.preset_id ?? optionType?.presetId ?? null;

// final-form rejects numeric path segments (e.g. variant_options.1)
export const variantOptionFieldKey = (presetId) => `preset_${presetId}`;

export const getVariantOptionSelections = (variantOptions, presetId) => {
  const options = variantOptions || {};
  const fieldKey = variantOptionFieldKey(presetId);
  if (Array.isArray(options[fieldKey])) return options[fieldKey];
  if (Array.isArray(options[presetId])) return options[presetId];
  return [];
};

export const mapOptionTypesToSortedGroups = (optionTypes, presetById) =>
  [...(optionTypes || [])]
    .sort((a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0))
    .map((optionType) => {
      const presetId = getOptionTypePresetId(optionType);
      const preset = presetById?.[presetId];
      const items = [...getOptionTypeValues(optionType)]
        .sort((a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0))
        .map((value) => ({
          optionValueId: value.id,
          name: value.label || value.name,
          label: value.label || value.name,
          sku_code: value.sku_code,
        }));
      return {
        presetId,
        presetName: preset?.name || optionType.name || String(presetId),
        presetSkuCode: preset?.sku_code,
        optionTypeId: optionType.id,
        items,
      };
    })
    .filter((group) => group.presetId != null);

export const buildVariantFormValues = async (
  optionTypes,
  presetById,
  loadCatalogForPreset
) => {
  const sorted = [...(optionTypes || [])].sort(
    (a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0)
  );
  const variant_options = {};
  const catalogPatch = {};

  await Promise.all(
    sorted.map(async (optionType) => {
      const presetId = getOptionTypePresetId(optionType);
      const preset = presetById[presetId];
      if (!preset) return;
      const catalog = await loadCatalogForPreset(preset);
      catalogPatch[presetId] = catalog;
      variant_options[variantOptionFieldKey(presetId)] = [...getOptionTypeValues(optionType)]
        .sort((a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0))
        .map((value) => value.sku_code)
        .filter((entry) => entry != null && entry !== "");
    })
  );

  return {
    variant_preset_ids: sorted
      .map((ot) => getOptionTypePresetId(ot))
      .filter((id) => id != null),
    variant_options,
    catalogPatch,
  };
};

export const buildSortGroupsFromForm = (
  values,
  presetById,
  catalog,
  existingGroups = []
) => {
  const selectedPresetIds = Array.isArray(values?.variant_preset_ids)
    ? values.variant_preset_ids
    : [];
  const variantSelections = values?.variant_options || {};

  // Preserve existing presets order, appending any newly selected presets
  const existingPresetIdMap = new Map();
  (existingGroups || []).forEach((g) => {
    existingPresetIdMap.set(String(g.presetId), g);
  });

  const orderedPresetIds = [
    ...(existingGroups || [])
      .map((g) => g.presetId)
      .filter((id) => selectedPresetIds.some((sid) => String(sid) === String(id))),
    ...selectedPresetIds.filter(
      (id) => !existingPresetIdMap.has(String(id))
    ),
  ];

  return orderedPresetIds
    .map((presetId) => {
      const preset = presetById[presetId];
      if (!preset) return null;
      const existingGroup = existingPresetIdMap.get(String(presetId));
      const selectedIds = getVariantOptionSelections(variantSelections, presetId);
      const options = catalog[presetId] || [];

      // Find all selected option objects from catalog
      const selectedOptions = selectedIds
        .map((id) =>
          options.find(
            (opt) =>
              String(opt.id) === String(id) ||
              String(opt.sku_code) === String(id)
          )
        )
        .filter(Boolean);

      // Preserve existing items order
      const existingItems = (existingGroup?.items || []).filter((item) =>
        selectedOptions.some(
          (opt) => String(getOptionSku(opt)) === String(item.sku_code)
        )
      );

      // Any newly added options not in existingItems get appended
      const existingSkuSet = new Set(
        existingItems.map((item) => String(item.sku_code))
      );
      const newItems = selectedOptions
        .filter((opt) => !existingSkuSet.has(String(getOptionSku(opt))))
        .map((opt) => ({
          name: getOptionLabel(opt),
          sku_code: getOptionSku(opt),
          label: getOptionLabel(opt),
        }));

      const items = [...existingItems, ...newItems];

      return {
        presetId,
        presetName: preset.name,
        presetSkuCode: preset.sku_code,
        optionTypeId: existingGroup?.optionTypeId,
        items,
      };
    })
    .filter(Boolean);
};

export const mapSizesToState = (list) =>
  (list || []).map((size, index) => ({
    key: size.id ? `size-${size.id}` : `size-${index}`,
    id: size.id,
    label: size.label,
    sku_code: size.sku_code,
  }));

export const normalizeSizeList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

export const buildSizesPayload = (sizes = []) => ({
  sizes: (sizes || []).map((size, index) => ({
    ...(size.id ? { id: size.id } : {}),
    label: size.label,
    sku_code: size.sku_code,
    sort_order: index + 1,
  })),
});

export const MAX_VARIANT_IMAGES = 3;
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
export const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];
export const ACCEPTED_IMAGE_ACCEPT = "image/jpeg,image/png,image/gif,image/webp,.jpg,.jpeg,.png,.gif,.webp";

export const normalizeVariantList = (payload) => {
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

export const normalizeImageList = (payload) => {
  if (Array.isArray(payload?.data)) {
    return payload.data.map(normalizeImageRecord).filter(Boolean);
  }
  if (Array.isArray(payload)) {
    return payload.map(normalizeImageRecord).filter(Boolean);
  }
  const single = normalizeImageRecord(payload?.data ?? payload);
  return single ? [single] : [];
};

export const normalizeImageRecord = (image) => {
  if (!image || typeof image !== "object") return null;
  const id = image.id ?? image.image_id ?? image.imageId;
  if (id == null) return null;
  return {
    id,
    url: image.url ?? image.image_url ?? "",
    sort_order: image.sort_order ?? image.sortOrder ?? null,
  };
};

export const getVariantDisplayName = (variant) => {
  const options = Array.isArray(variant?.options) ? variant.options : [];
  const labels = options
    .map((opt) => `${opt.option_type_name || ""} ${opt.option_value_label || ""}`.trim())
    .filter(Boolean);
  if (labels.length) return labels.join(" / ");
  return variant?.sku || `Variant ${variant?.id ?? ""}`;
};

export const mapVariantImages = (images) =>
  [...(Array.isArray(images) ? images : [])]
    .map(normalizeImageRecord)
    .filter(Boolean)
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .map((image, index) => ({
      id: image.id,
      url: image.url,
      sort_order: image.sort_order ?? index + 1,
    }));

export const mapVariantsToState = (list) =>
  (list || []).map((variant) => ({
    id: variant.id,
    sku: variant.sku,
    is_active: variant.is_active ?? true,
    name: getVariantDisplayName(variant),
    options: variant.options || [],
    price: variant.price ?? null,
    size_prices: Array.isArray(variant.size_prices) ? variant.size_prices : [],
    hybrid_metal: variant.hybrid_metal || null,
    images: mapVariantImages(variant.images),
    pending: [],
  }));

export const fetchPreciousMetalTypes = async () => {
  const response = await axios.get(`${apiUrl}/precious-metal-types`, {
    params: { "page[size]": 1000 },
  });
  const data = response?.data?.data ?? response?.data;
  const list = Array.isArray(data) ? data : [];
  return sortAlphabetically(list, "name");
};

export const fetchActiveMetalPrices = async (date = new Date()) => {
  const response = await axios.get(`${apiUrl}/precious-lbma-lme-prices`, {
    params: { date: format(date, "yyyy-MM-dd") },
  });
  const activePrices = response?.data?.active_prices;
  return Array.isArray(activePrices) ? activePrices : [];
};

export const updateVariantPrice = async (variantId, price) => {
  const response = await axios.put(`${apiUrl}/variants/${variantId}/price`, {
    price,
  });
  return response?.data?.data ?? response?.data;
};

export const updateVariantSizePrice = async (variantId, sizeId, price) => {
  const response = await axios.put(`${apiUrl}/variants/${variantId}/price`, {
    size_prices: [{ size_id: sizeId, price }],
  });
  return response?.data?.data ?? response?.data;
};

export const updateVariantMetal = async (variantId, metalPayload) => {
  const response = await axios.put(
    `${apiUrl}/variants/${variantId}/price`,
    metalPayload
  );
  return response?.data?.data ?? response?.data;
};

export const updateVariantActiveStatus = async (variantId, isActive) => {
  const response = await axios.put(`${apiUrl}/variants/${variantId}`, {
    is_active: Boolean(isActive),
  });
  return response?.data?.data ?? response?.data;
};

export const validateImageFile = (file) => {
  if (!file) return "No file selected";
  if (file.size > MAX_IMAGE_BYTES) return `${file.name} exceeds 10MB`;
  const typeOk = ACCEPTED_IMAGE_TYPES.includes(file.type);
  const extOk = /\.(jpe?g|png|gif|webp)$/i.test(file.name || "");
  if (!typeOk && !extOk) return `${file.name} must be jpeg, png, gif, or webp`;
  return null;
};

export const downloadBlobFile = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const getContentDispositionFilename = (header, fallback) => {
  if (!header) return fallback;
  const match = /filename\*?=(?:UTF-8''|["']?)([^"';\n]+)/i.exec(header);
  if (!match?.[1]) return fallback;
  try {
    return decodeURIComponent(match[1].replace(/['"]/g, ""));
  } catch {
    return match[1].replace(/['"]/g, "");
  }
};

export const exportVariantImagesCsvFilename = (productId) =>
  `simple-product-${productId}-variant-images.csv`;

export const exportVariantImagesCsv = async (productId) => {
  const response = await axios.get(
    `${apiUrl}/simple-products/${productId}/variant-images/export`,
    { responseType: "blob" }
  );
  const contentType = String(response.headers?.["content-type"] || "");
  if (contentType.includes("application/json")) {
    const text = await response.data.text();
    let message = "Failed to export variant images CSV";
    try {
      const payload = JSON.parse(text);
      message = payload?.message || payload?.errors?.message || message;
    } catch {
      message = text || message;
    }
    throw new Error(message);
  }
  const filename = getContentDispositionFilename(
    response.headers?.["content-disposition"],
    exportVariantImagesCsvFilename(productId)
  );
  downloadBlobFile(response.data, filename);
  return filename;
};

const parseCsvLine = (line) => {
  const cells = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    const next = line[i + 1];
    if (char === '"' && inQuotes && next === '"') {
      current += '"';
      i += 1;
      continue;
    }
    if (char === '"') {
      inQuotes = !inQuotes;
      continue;
    }
    if (char === "," && !inQuotes) {
      cells.push(current);
      current = "";
      continue;
    }
    current += char;
  }
  cells.push(current);
  return cells;
};

export const parseVariantImagesCsvText = (text) => {
  const lines = String(text || "")
    .replace(/^\uFEFF/, "")
    .split(/\r\n|\n|\r/)
    .filter((line) => line.trim().length > 0);
  if (!lines.length) {
    return { rows: [], filenames: [], header: [] };
  }

  const header = parseCsvLine(lines[0]).map((cell) => cell.trim().toLowerCase());
  const variantIdx = header.indexOf("variant_id");
  const optionsIdx = header.indexOf("options");
  const filenameIdx = header.indexOf("image_filename");
  if (variantIdx < 0 || filenameIdx < 0) {
    throw new Error("CSV must include variant_id and image_filename columns");
  }

  const rows = [];
  const filenames = [];
  for (let i = 1; i < lines.length; i += 1) {
    const cells = parseCsvLine(lines[i]);
    const filename = String(cells[filenameIdx] || "").trim();
    if (!filename) continue;
    rows.push({
      variant_id: String(cells[variantIdx] || "").trim(),
      options: optionsIdx >= 0 ? String(cells[optionsIdx] || "").trim() : "",
      image_filename: filename,
    });
    filenames.push(filename);
  }

  return {
    header,
    rows,
    filenames: [...new Set(filenames)],
  };
};

export const readFileAsText = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Failed to read CSV file"));
    reader.readAsText(file);
  });

export const buildFileMapByName = (files = []) => {
  const map = {};
  (files || []).forEach((file) => {
    if (file?.name) map[file.name] = file;
  });
  return map;
};

export const getCsvImageMatchSummary = (csvFilenames = [], fileMap = {}) => {
  const matched = [];
  const missingInSelection = [];
  csvFilenames.forEach((name) => {
    if (fileMap[name]) matched.push(name);
    else missingInSelection.push(name);
  });
  const unusedSelected = Object.keys(fileMap).filter(
    (name) => !csvFilenames.includes(name)
  );
  return { matched, missingInSelection, unusedSelected };
};

export const presignVariantImages = async (productId, filenames) => {
  const response = await axios.post(
    `${apiUrl}/simple-products/${productId}/variant-images/presign`,
    { filenames }
  );
  const uploads = response?.data?.uploads ?? response?.data?.data?.uploads ?? [];
  if (!Array.isArray(uploads) || !uploads.length) {
    throw new Error("No presigned upload targets were returned");
  }
  return uploads;
};

export const uploadFileToS3Presign = async (uploadTarget, file) => {
  const formData = new FormData();
  const fields = uploadTarget?.fields || {};
  // S3 POST policy expects policy fields first, then the file last.
  Object.keys(fields).forEach((key) => {
    formData.append(key, fields[key]);
  });
  formData.append("file", file);

  // Use fetch (not axios) so the app's global Bearer Authorization is not sent.
  // S3 rejects Authorization: Bearer ... with InvalidArgument / Unsupported Authorization Type.
  const response = await fetch(uploadTarget.url, {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    const detail = await response.text().catch(() => "");
    throw new Error(
      detail
        ? `S3 upload failed (${response.status}): ${detail.slice(0, 300)}`
        : `S3 upload failed (${response.status})`
    );
  }

  return response;
};

export const importVariantImagesCsv = async (productId, csvFile) => {
  const formData = new FormData();
  formData.append("csv", csvFile);
  const response = await axios.post(
    `${apiUrl}/simple-products/${productId}/variant-images/import`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return response?.data?.report ?? response?.data?.data?.report ?? [];
};

export const exportVariantPricesCsvFilename = (productId) =>
  `simple-product-${productId}-variant-prices.csv`;

export const exportVariantPricesCsv = async (
  productId,
  hasUniformSizePricing = true,
  isVariantHybrid = false
) => {
  const response = await axios.get(
    `${apiUrl}/simple-products/${productId}/variant-prices/export`,
    {
      responseType: "blob",
      params: {
        has_uniform_size_pricing: hasUniformSizePricing ? 1 : 0,
        ...(isVariantHybrid ? { hybrid_pricing: 1 } : {}),
      },
    }
  );
  const contentType = String(response.headers?.["content-type"] || "");
  if (contentType.includes("application/json")) {
    const text = await response.data.text();
    let message = "Failed to export variant prices CSV";
    try {
      const payload = JSON.parse(text);
      message = payload?.message || payload?.errors?.message || message;
    } catch {
      message = text || message;
    }
    throw new Error(message);
  }
  const filename = getContentDispositionFilename(
    response.headers?.["content-disposition"],
    exportVariantPricesCsvFilename(productId)
  );
  downloadBlobFile(response.data, filename);
  return filename;
};

export const importVariantPricesCsv = async (
  productId,
  csvFile,
  hasUniformSizePricing = true
) => {
  const formData = new FormData();
  formData.append("csv", csvFile);
  const response = await axios.post(
    `${apiUrl}/simple-products/${productId}/variant-prices/import`,
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
      params: {
        has_uniform_size_pricing: hasUniformSizePricing ? 1 : 0,
      },
    }
  );
  return response?.data?.report ?? response?.data?.data?.report ?? [];
};

export const DYNAMIC_PRICING_TYPE_COMPONENT = "component";
export const DYNAMIC_PRICING_TYPE_VOLUMETRIC = "volumetric";

export const SIZE_ADJUSTMENT_TYPE_EQUAL = "equal";
export const SIZE_ADJUSTMENT_TYPE_VARIABLE = "variable";

export const SIZE_ADJUSTMENT_UNIT_PERCENTAGE = "percentage";
export const SIZE_ADJUSTMENT_UNIT_VOLUME = "volume";

export const mapOptionTypesForPricing = (optionTypes = []) =>
  [...optionTypes]
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .map((optionType) => ({
      id: optionType.id,
      name:
        optionType.name ||
        optionType.option_type_name ||
        optionType.preset_name ||
        `Option type ${optionType.id}`,
      values: getOptionTypeValues(optionType)
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
        .map((value) => ({
          id: value.id,
          label: value.label || value.name || value.option_value_label,
        })),
    }))
    .filter((optionType) => optionType.id != null);

export const normalizeDynamicPricingList = (payload) => {
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

export const fetchDynamicPricing = async (productId) => {
  const response = await axios.get(
    `${apiUrl}/simple-products/${productId}/dynamic-pricing`
  );
  return normalizeDynamicPricingList(response?.data);
};

export const fetchProductCompletion = async (productId) => {
  const response = await axios.get(
    `${apiUrl}/simple-products/${productId}/completion`
  );
  return response?.data?.data ?? response?.data;
};

export const fetchComponentsCatalog = async () => {
  const response = await axios.get(`${apiUrl}/components`, {
    params: { "page[number]": 1, "page[size]": 1000 },
  });
  const list = response?.data?.data ?? response?.data ?? [];
  return Array.isArray(list) ? list : [];
};

export const fetchRingMaterialsCatalog = async () => {
  const response = await axios.get(`${apiUrl}/custom-pricing/ring-materials`, {
    params: { "page[number]": 1, "page[size]": 1000 },
  });
  const list = response?.data?.data ?? response?.data ?? [];
  return Array.isArray(list) ? list : [];
};

export const ensureDynamicProductPricing = async (productId, formData = {}) => {
  const payload = buildPricingPayload({
    ...formData,
    price_type: PRICE_TYPE_DYNAMIC,
  });
  await axios.put(`${apiUrl}/simple-products/${productId}`, payload);
};

export const fetchOptionValueDynamicPricing = async (optionValueId) => {
  const response = await axios.get(`${apiUrl}/option-values/${optionValueId}/dynamic-pricing`);
  return response?.data?.data ?? response?.data;
};

export const upsertOptionValueDynamicPricing = async (
  optionValueId,
  body,
  method = "put"
) => {
  const normalizedMethod = String(method || "put").toLowerCase() === "post" ? "post" : "put";
  const response = await axios({
    method: normalizedMethod,
    url: `${apiUrl}/option-values/${optionValueId}/dynamic-pricing`,
    data: body,
  });
  return response?.data?.data ?? response?.data;
};

export const clearOptionValueDynamicPricing = async (optionValueId) => {
  await axios.delete(`${apiUrl}/option-values/${optionValueId}/dynamic-pricing`);
};

export const notifyRequestError = (notify, error, fallback = "An error occurred") => {
  const errors = error?.response?.data?.errors ?? {};
  if (errors && typeof errors === "object" && "message" in errors) {
    notify(errors.message, { type: "warning" });
    return;
  }
  const key = Object.keys(errors)[0];
  if (key && errors[key]?.[0]) {
    notify(errors[key][0], { type: "warning" });
    return;
  }
  notify(error?.response?.data?.message || fallback, { type: "warning" });
};

export { mapStoreOptions };
